import React, { useEffect, useState } from "react";
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Button,
  Flex,
  Heading,
  useToast,
  Select, // 🔹 added
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import axios from "axios";
import * as mod from "../../url";
import * as XLSX from "xlsx";
import Sidebar from "../Sidebar";
import Navbar from "../Navbar/Navbar";

const MeetUsers = () => {
  const [meetUsers, setMeetUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortOption, setSortOption] = useState(""); // 🔹 sorting state

  const toast = useToast();
  const storedData = JSON.parse(sessionStorage.getItem("superAdminLawvs"));
  const token = storedData?.data?.token;
  const config = { headers: { Authorization: `${token}` } };
  const SIDEBAR_WIDTH = "250px";

  // Fetch Meet Users
  const GetMeetusers = async () => {
    try {
      const { data } = await axios.get(
        `${mod.api_url}/api/v1/meetusers/get/meet`,
        config
      );
      setMeetUsers(data);
      setFilteredUsers(data);
    } catch (error) {
      console.error("Error fetching meet users:", error);
      toast({
        title: "Error fetching meet users",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    GetMeetusers();
  }, []);

  // Filter + Sort by name/date
  useEffect(() => {
    let filtered = [...meetUsers];

    // 🔹 Filter by search
    if (search.trim()) {
      filtered = filtered.filter(
        (u) =>
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase()) ||
          u.mobile?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // 🔹 Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter((u) => {
        const created = new Date(u.createdAt);
        return created >= start && created <= end;
      });
    }

    // 🔹 Sort data
    if (sortOption === "name-asc") {
      filtered.sort((a, b) => a.name?.localeCompare(b.name));
    } else if (sortOption === "name-desc") {
      filtered.sort((a, b) => b.name?.localeCompare(a.name));
    } else if (sortOption === "date-new") {
      filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortOption === "date-old") {
      filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    }

    setFilteredUsers(filtered);
  }, [search, startDate, endDate, meetUsers, sortOption]);

  // Export to Excel
  const exportToExcel = () => {
    if (filteredUsers.length === 0) {
      toast({
        title: "No data to export",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(filteredUsers);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MeetUsers");
    XLSX.writeFile(workbook, "MeetUsers.xlsx");
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box mt="70px" ml={{ base: 0, md: SIDEBAR_WIDTH }} p={3}>
        <Heading
          color="teal.600"
          textAlign="center"
          fontSize="2xl"
          mb={4}
          size="md"
          bg="#D29B3F"
          p={3}
        >
          Meet Users
        </Heading>

        {/* 🔹 Filter Section */}
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          gap={4}
          mb={6}
          bg="white"
          p={4}
          borderRadius="lg"
          shadow="sm"
        >
          <InputGroup w={{ base: "100%", md: "30%" }}>
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, email, or mobile"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              bg="gray.100"
              borderRadius="md"
            />
          </InputGroup>

          <Flex gap={3} align="center" w={{ base: "100%", md: "40%" }}>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              bg="gray.100"
              borderRadius="md"
            />
            <Text>to</Text>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              bg="gray.100"
              borderRadius="md"
            />
          </Flex>

          {/* 🔹 Sort Dropdown */}
          <Select
            placeholder="Sort By"
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            w={{ base: "100%", md: "20%" }}
            bg="gray.100"
            borderRadius="md"
          >
            <option value="name-asc">Name (A → Z)</option>
            <option value="name-desc">Name (Z → A)</option>
            <option value="date-new">Date (Newest First)</option>
            <option value="date-old">Date (Oldest First)</option>
          </Select>

          <Button
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            variant="solid"
            onClick={exportToExcel}
            w={{ base: "100%", md: "auto" }}
          >
            Export Excel
          </Button>
        </Flex>

        {/* Data Table */}
        <Box bg="white" borderRadius="lg" shadow="md" overflowX="auto">
          <Text fontSize="sm" mb={1} textAlign={"right"} align={"right"}>
            Total Record: {meetUsers?.length}
          </Text>
          {filteredUsers.length === 0 ? (
            <Text textAlign="center" p={6} color="gray.500">
              No meet users found.
            </Text>
          ) : (
            <Table variant="striped" colorScheme="gray">
              <Thead bg="teal.500">
                <Tr>
                  <Th color="white">#</Th>
                  <Th color="white">Name</Th>
                  <Th color="white">Email</Th>
                  <Th color="white">Mobile</Th>
                  <Th color="white">Comments</Th>
                  <Th color="white">Created At</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers.map((user, index) => (
                  <Tr key={user._id}>
                    <Td>{index + 1}</Td>
                    <Td fontWeight="bold">{user.name}</Td>
                    <Td>{user.email}</Td>
                    <Td>{user.mobile}</Td>
                    <Td>{user.comments}</Td>
                    <Td>
                      {new Date(user.createdAt).toLocaleString("en-IN", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </Box>
    </>
  );
};

export default MeetUsers;
